import React, { ReactElement } from 'react';
import { Container, Box, Typography, Paper, Stack } from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';

interface AdminLayoutProps {
  title: string;
  description: string;
  children?: React.ReactNode;
  filters?: React.ReactNode;
  resultsSummary?: React.ReactNode;
  table?: React.ReactNode;
  pagination?: React.ReactNode;
  emptyState?: React.ReactNode;
  error?: string | null;
}

export function AdminLayout({
  title,
  description,
  children,
  filters,
  resultsSummary,
  table,
  pagination,
  emptyState,
  error
}: AdminLayoutProps): ReactElement {
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </Box>

      {/* Error State */}
      {error && (
        <Paper sx={{ 
          p: 2, 
          mb: 3, 
          borderRadius: 2, 
          bgcolor: 'error.light',
          color: 'error.contrastText'
        }}>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}

      {/* Filters Section */}
      {filters && (
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2, 
          boxShadow: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
              Filters
            </Typography>
          </Stack>
          {filters}
        </Paper>
      )}

      {/* Results Summary */}
      {resultsSummary}

      {/* Table */}
      {table}

      {/* Pagination */}
      {pagination}

      {/* Empty State */}
      {emptyState}

      {/* Fallback for custom children */}
      {children}
    </Container>
  );
}
